import React, { CSSProperties } from 'react';
import { usePreviewContext } from '@/components/PreviewProvider';
import { useTranslation } from 'react-i18next';

interface EditableImageProps {
  page: string;
  section: string;
  imageKey: string;
  src: string | null;
  alt?: string | null;
  className?: string;
  style?: CSSProperties;
  fallbackSrc?: string;
  loading?: 'lazy' | 'eager';
  children?: React.ReactNode;
}

/**
 * Wraps an image (or children containing an image) with visual editing data attributes in preview mode.
 * In preview mode, overlays draft image URLs from visualDraftsMap when available.
 */
export function EditableImage({
  page,
  section,
  imageKey,
  src,
  alt,
  className,
  style,
  fallbackSrc,
  loading,
  children,
}: EditableImageProps) {
  const { isPreview, canEdit, visualDraftsMap } = usePreviewContext();
  const { i18n } = useTranslation();
  const isEditable = isPreview && canEdit;

  // Check for draft override in preview mode
  let resolvedSrc = src || fallbackSrc || '';
  let resolvedAlt = alt || '';

  if (isPreview && visualDraftsMap) {
    const draftKey = `${page}-${section}-${imageKey}`;
    const draft = visualDraftsMap.get(draftKey);
    if (draft) {
      const locale = i18n.language as 'nl' | 'en' | 'de';
      const urlField = `image_url_${locale}` as keyof typeof draft;
      const altField = `alt_text_${locale}` as keyof typeof draft;
      if (draft[urlField]) {
        resolvedSrc = draft[urlField] as string;
      }
      if (draft[altField]) {
        resolvedAlt = draft[altField] as string;
      }
    }
  }

  const dataAttrs = isEditable
    ? {
        'data-visual-page': page,
        'data-visual-section': section,
        'data-visual-key': imageKey,
      }
    : {};

  // If children are provided (e.g. wrapping a GlassyImage), wrap in a div with data attrs
  if (children) {
    return (
      <div {...dataAttrs} className={className} style={style}>
        {children}
      </div>
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={resolvedAlt}
      className={className}
      style={style}
      loading={loading}
      {...dataAttrs}
    />
  );
}
